import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { useNavigate, useParams, Location } from 'react-router';
import PageHeader from '../../../../shared/components/PageHeader';
import Topics from './Topics';
import OperationContext from '../context';
import useService from '../../../../shared/hooks/useService';
import toolkitService from '../../../../services/toolkitService';
import { SpinnerArea, Button } from '../../../../shared/components';
import operationService from '../../../../services/operationService';
import {
  Toolkit,
  FinishToolkitState,
  AnswerMultipleChoice,
  MultipleChoiceWrapper,
  ToolkitCalculatedValues,
  ToolkitTypes,
} from '../../../../services/types/Toolkit';
import ToolkitsContext from '../ToolkitList/context';
import { IdRoute } from '../../../../types';
import { dateTimeFormat, getTypeLinkName } from '../../../../shared/misc/utils';
import Context from '../../../App/MainProvider/context';
import RouteLeavingGuard from '../../../App/RouteLeavingGuard';
import FinishModal from './FinishModal';
import Modal from '../../../../shared/components/Modal';
import HistoryLink from '../HistoryLink';
import NotificationContext from '../../../Notifications/NotificationContext';

interface Props {
  toolkitData: Toolkit | null;
  type: string;
}

const ToolkitComp: React.FC<Props> = (props: Props) => {
  const { toolkitData, type } = props;
  const operationContext = useContext(OperationContext);
  const { operation, reload: reloadDataOperation } = operationContext;

  // if manually change type from url -> return null
  const toolkitType = toolkitData ? getTypeLinkName(toolkitData.toolkitType) : type;
  if (toolkitData && toolkitType !== type) {
    return null;
  }

  const navigate = useNavigate();

  const { t } = useTranslation();
  const { service, notify, inSuperUserMode } = useContext(Context);

  const { toolkits } = useContext(ToolkitsContext);

  const { reload: reloadNotifications } = useContext(NotificationContext);

  const [toolkit, loading] = useService(() =>
    toolkitData
      ? toolkitService.getToolkitForId(toolkitData.id)
      : toolkitService.getToolkit(type, operation.id)
  );

  const [toolkitAnswersState, answersLoading, setToolkitAnswersState] = useService(() =>
    operationService.getToolkitAnswers({
      operationId: operation.id,
      toolkitId: toolkitData ? toolkitData.id : null,
    })
  );

  const [toolkitDirt, setToolkitDirt] = useState(false);
  const [saved, setSaved] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculatedValuesData, setCalculatedValues] = useState<ToolkitCalculatedValues>({
    impactCategoryData: {
      impactCategory: '',
      message: '',
      hasCompliance: false,
      toolkitRationales: [],
    },
    disasterRiskData: {
      disasterRisk: '',
      toolkitRationales: [],
    },
    esrrData: {
      esrr: '',
      toolkitRationales: [],
      changes: false,
      justification: '',
      lastCompass: false,
    },
    esps: '',
  });
  const [isGettingCalculatedValues, setIsGettingCalculatedValues] = useState(false);
  const [finishModalOpened, setFinishModalOpened] = useState(false);

  const handleInputChange = (
    value: number,
    questionId: number,
    topicId: number,
    choiceWrapper: MultipleChoiceWrapper | null
  ) => {
    if (toolkitAnswersState != null) {
      const toolkitDataStateClone = [...toolkitAnswersState];
      const answer = toolkitDataStateClone.find((x) => x.questionId === questionId);
      if (answer) {
        answer.value = value;

        if (choiceWrapper) {
          const choice: AnswerMultipleChoice = { answerId: answer.id, ...choiceWrapper.choice };

          const choiceIndex = answer.answerMultipleChoices.findIndex(
            (x) => x.multipleChoiceId === choice.multipleChoiceId
          );

          if (!choiceWrapper.value && choice.multipleChoiceId === -1) {
            answer.answerMultipleChoices = [];
          } else if (choiceIndex < 0 && choice.multipleChoiceId !== -1) {
            answer.answerMultipleChoices.push(choice);
          } else if (choiceWrapper.value) {
            answer.answerMultipleChoices[choiceIndex] = { ...choice };
          } else {
            answer.answerMultipleChoices.splice(choiceIndex, 1);
          }
        }
      } else {
        toolkitDataStateClone.push({ value, topicId, questionId, answerMultipleChoices: [] });
      }
      setToolkitAnswersState(toolkitDataStateClone);
      setToolkitDirt(true);
    }
  };

  const saveData = (location?: Location | null) => {
    if (!toolkit) {
      return;
    }
    setIsSubmitting(true);
    if (toolkitAnswersState) {
      const dto = {
        operationId: operation.id,
        toolkitId: null,
        answers: toolkitAnswersState,
      };
      const callback = () => {
        setIsSubmitting(false);
        setToolkitDirt(false);
        setSaved(true);
        notify('Toolkit', t('successSave'), 'success');
        reloadNotifications();
        if (location) {
          navigate(location);
        }
      };

      service(() => operationService.postToolkitAnswers(dto, toolkit.toolkitType), { callback });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveData();
  };

  const isDraft = () => {
    if (operation.isEspf) {
      return toolkitData != null ? toolkitData.pdfCreatedBy == null : true;
    }

    return !toolkitData;
  };
  const noDraftTitleExtension = toolkitData ? `V.${toolkitData.version}` : '';

  const confirmCreation = () => {
    setModalOpened(false);
    if (toolkitData) {
      service(() => operationService.createToolkitDraftFromVersion(toolkitData.id), {
        callback: () => {
          setIsSubmitting(false);
          notify('Toolkit', t('successCreatedDraft'), 'success');
          reloadNotifications();
          navigate(`/operations/${operation.number}/toolkit/form`);
        },
      });
    }
  };

  const showFinishModal = () => setFinishModalOpened(true);
  const closeFinishModal = () => setFinishModalOpened(false);

  const handleFinishButtonClick = () => {
    if (!toolkit) {
      return;
    }
    // setIsGettingCalculatedValues(true);
    if (toolkitAnswersState) {
      service(
        () =>
          operationService.getCalculatedValues(
            {
              operationId: operation.id,
              toolkitId: null,
              answers: toolkitAnswersState,
            },
            toolkit.toolkitType
          ),
        {
          callback: (cat) => {
            // setIsGettingCalculatedValues(false);
            setToolkitDirt(false);
            if (cat) {
              setCalculatedValues({ ...cat });
              showFinishModal();
            } else {
              notify('Toolkit', t('failGettingImpact'), 'danger');
            }
          },
          loader: setIsGettingCalculatedValues,
        }
      );
    }
  };

  const submitFinishToolkit = (values: FinishToolkitState, updateCompliance = false) => {
    if (!toolkit) {
      return;
    }
    window.scrollTo(0, 0);

    service(
      () =>
        operationService.finishToolkit(
          {
            ...values,
            operationId: operation.id,
            updateCompliance,
          },
          toolkit.toolkitType
        ),
      {
        callback: () => {
          closeFinishModal();

          if (!operation.isEspf) {
            navigate(`/operations/${operation.number}/toolkit/list`);
          } else {
            navigate(`/operations/${operation.number}/toolkit/espf/list`);
          }
          notify('Toolkit', t('successFinishToolkit'), 'success');
          reloadDataOperation();
          reloadNotifications();
        },
      }
    );
  };

  const getBacktimes = () => (operation.isEspf ? 0 : 2);

  const getBackUrl = () => {
    if (operation.isEspf) {
      if (!saved && (toolkitAnswersState === null || toolkitAnswersState.length === 0)) {
        return `/operations/${operation.number}/toolkit/espf/financial-form`;
      }

      return `/operations/${operation.number}/toolkit/espf/list`;
    }

    return undefined;
  };

  const getHistoryUrl = () => {
    if (operation.isEspf) {
      return `/operations/${operation.number}/toolkit/espf/list`;
    }

    return `/operations/${operation.number}/toolkit/list`;
  };

  return (
    <>
      <RouteLeavingGuard
        when={toolkitDirt}
        shouldBlockNavigation={() => toolkitDirt}
        onSave={(location) => saveData(location)}
      />
      {toolkitData && (
        <Modal
          visible={modalOpened}
          setVisible={setModalOpened}
          actions={[
            { text: t('cancel') },
            { text: t('createConfirmToolkitDraftBtn'), action: () => confirmCreation() },
          ]}
        >
          <p>
            <b>{t('createToolkitDraftMsgTitle')}</b>
          </p>
          <p>
            {t('createToolkitDraftMsgContent', {
              version: toolkitData.version,
              date: dateTimeFormat(toolkitData.createdOn),
            })}
          </p>
        </Modal>
      )}
      {toolkit && calculatedValuesData && (
        <FinishModal
          calculatedValues={calculatedValuesData}
          operationNumber={operation.number}
          visible={finishModalOpened}
          isEspf={operation.isEspf}
          setVisible={setFinishModalOpened}
          handleSubmit={submitFinishToolkit}
          toolkitType={toolkit.toolkitType}
        />
      )}

      <Container className="dashboard">
        <PageHeader
          title={`Operation ${operation.number} / Toolkit ${noDraftTitleExtension}`}
          side={
            !operation.isEspf &&
            (!isDraft()
              ? (operation.isMember ||
                  inSuperUserMode() ||
                  operation.userTaskDelegations.includes('TOOLKIT')) && (
                  <Button
                    text={t('createToolkitDraftBtn')}
                    className="btn-primary"
                    onClick={() => setModalOpened(true)}
                    htmlId="btn_title"
                  />
                )
              : toolkits.length > 1 && (
                  <>
                    <div className="mr-auto" />
                    <HistoryLink
                      text={t('viewHistory')}
                      versions={toolkits.length - 1}
                      to={getHistoryUrl()}
                    />
                  </>
                ))
          }
          backTimes={getBacktimes()}
          backUrl={getBackUrl()}
        />

        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="card__title">
                  <h5>{t('topicsHeader')}:</h5>
                </div>
                <SpinnerArea loading={loading || answersLoading}>
                  {toolkit && toolkitAnswersState && (
                    <Topics
                      topics={toolkit.topics}
                      title={toolkit.toolkitType === ToolkitTypes.ESPS ? 'ESPS' : 'Topics'}
                      isDraft={isDraft()}
                      isEspf={operation.isEspf}
                      isSubmitting={isSubmitting}
                      isGettingImpact={isGettingCalculatedValues}
                      toolkitAnswers={toolkitAnswersState}
                      handleSubmit={handleSubmit}
                      handleInputChange={handleInputChange}
                      handleFinishButtonClick={handleFinishButtonClick}
                    />
                  )}
                </SpinnerArea>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

const ToolkitWrapper = ({ type }: { type: string }) => {
  const { id: toolkitId } = useParams() as unknown as IdRoute;
  const operationContext = useContext(OperationContext);
  const { toolkits } = useContext(ToolkitsContext);

  let toolkit = null;
  if (toolkitId) {
    toolkit = toolkits.find((x) => x.id.toString() === toolkitId);
    if (!toolkit) {
      return null;
    }
  } else if (
    toolkits.length > 0 &&
    !toolkits.some((x) => x.documentList.length === 0) &&
    !operationContext.operation.isEspf
  ) {
    return null;
  }

  return <ToolkitComp toolkitData={toolkit} type={type} />;
};

export default ToolkitWrapper;
